import { Button } from './Button'
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { clearMeta } from '../state/db'

/**
 * What shows instead of a blank page when rendering fails.
 *
 * Without it, a fault that is stored, such as a bad setting from an imported
 * session, would break the application on every reload. Resetting the view
 * settings removes the likeliest cause and keeps every saved assessment.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, { error?: Error }> {
  state: { error?: Error } = {}

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(error, info.componentStack)
  }

  render() {
    if (!this.state.error) return this.props.children
    return (
      <div className="error-screen" role="alert">
        <h1>Something went wrong</h1>
        <p>
          This page could not be shown. Your saved assessments are not affected. If reloading does
          not help, resetting the view settings (filters, selections and similar) usually does.
        </p>
        <p className="small faint">{this.state.error.message}</p>
        <div className="row">
          <Button tone="primary" label="Reload" onClick={() => location.reload()} />
          <Button
            label="Reset view settings and reload"
            onClick={() => {
              void clearMeta()
                .catch(() => undefined)
                .then(() => location.reload())
            }}
          />
        </div>
      </div>
    )
  }
}
